import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import { ref, onMounted, watch } from 'vue'
import { EditorView, basicSetup } from 'codemirror'
import { javascript } from '@codemirror/lang-javascript'
import { autocompletion, CompletionContext, CompletionResult } from '@codemirror/autocomplete'
import { githubDark } from '@fsegurai/codemirror-theme-github-dark'

interface SchemaField {
  key: string
  type: 'string' | 'number' | 'boolean' | 'array'
  description: string
  items?: {
    type: string
    description: string
  }
}

const defaultCode = `function(context) {
  // Ensure success before processing
  if (!context.success) return;
  
  // Use data from context to update your site
  updateProductPage(context.data)
  return true;
}`

// Custom completions for the context object

export default /*@__PURE__*/_defineComponent({
  __name: 'CodeEditor',
  props: {
    modelValue: {},
    schema: {}
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const editor = ref<HTMLElement>()
let view: EditorView

function contextCompletions(context: CompletionContext): CompletionResult | null {
  const word = context.matchBefore(/[\w.]*/)
  if (!word) return null

  const completions = [
    {
      label: 'context',
      type: 'variable',
      detail: 'The context object passed to the callback function',
      info: 'Contains search_id, success, and data properties'
    },
    {
      label: 'context.search_id',
      type: 'property',
      detail: 'string',
      info: 'Unique identifier for the search'
    },
    {
      label: 'context.success',
      type: 'property',
      detail: 'boolean',
      info: 'Whether the operation was successful'
    },
    {
      label: 'context.data',
      type: 'property',
      detail: 'object',
      info: 'Contains the data based on your schema'
    }
  ]

  // Add schema-based completions
  if (props.schema) {
    props.schema.forEach(field => {
      completions.push({
        label: `context.data.${field.key}`,
        type: 'property',
        detail: field.type + (field.type === 'array' ? `<${field.items?.type || 'any'}>` : ''),
        info: field.description
      })
    })
  }

  return {
    from: word.from,
    options: completions,
    validFor: /^\w*$/
  }
}

onMounted(() => {
  if (!editor.value) return

    view = new EditorView({
      doc: props.modelValue || defaultCode,
    extensions: [
      basicSetup,
      javascript(),
      autocompletion({
        override: [contextCompletions]
      }),
      githubDark,
      EditorView.updateListener.of(update => {
        if (update.docChanged) {
          emit('update:modelValue', update.state.doc.toString())
        }
      }),
      EditorView.theme({
        '&': {
          height: '100%',
          fontSize: '14px'
        },
        '.cm-content': {
          fontFamily: 'monospace',
          padding: '8px'
        },
        '.cm-gutters': {
          backgroundColor: 'transparent',
          border: 'none'
        },
        '.cm-activeLineGutter': {
          backgroundColor: 'transparent'
        },
        '.cm-line': {
          padding: '0 8px'
        }
      })
    ],
    parent: editor.value
  })
})

// Watch for external changes to modelValue
watch(() => props.modelValue, (newValue) => {
  const currentValue = view.state.doc.toString()
  if (newValue !== currentValue) {
    view.dispatch({
      changes: {
        from: 0,
        to: currentValue.length,
        insert: newValue
      }
    })
  }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "editor",
    ref: editor,
    class: "w-full h-[415px] bg-dark border border-gray-700 rounded-lg text-gray-100 font-mono text-sm"
  }, null, 512))
}
}

})